import { useState, useMemo } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { PhoneCalendarButton } from "@/components/PhoneCalendarButton";
import ActivityDetailDialog from "@/components/ActivityDetailDialog";
import ProjectView from "@/components/ProjectView";
import { ChevronLeft, ChevronRight, CalendarClock, Plus, Loader2, Trash2, Check, Phone, MapPin, ChevronsUpDown, UserRound, FileText, Hammer, Briefcase, ExternalLink, Mail, StickyNote, ClipboardList, AlertOctagon } from "lucide-react";

const DAY_START = 6;
const DAY_END = 18;
const HOUR_PX = 56;
const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);

// Colour coding for the three event kinds — bookings stay cyan/violet per
// booking_type, jobs are always emerald, assigned tasks are always amber.
// This is the single source of truth for the calendar legend.
const TYPE_COLOR = {
  callback: "bg-amber-100 border-amber-400 text-amber-900",
  site_quote: "bg-violet-100 border-violet-400 text-violet-900",
  installation: "bg-cyan-100 border-cyan-400 text-cyan-900",
  install: "bg-cyan-100 border-cyan-400 text-cyan-900",
  measure: "bg-sky-100 border-sky-400 text-sky-900",
  delivery: "bg-amber-100 border-amber-400 text-amber-900",
  repair: "bg-rose-100 border-rose-400 text-rose-900",
  other: "bg-slate-100 border-slate-400 text-slate-800",
};
const JOB_COLOR = "bg-emerald-100 border-emerald-500 text-emerald-900";
const TASK_COLOR = "bg-amber-50 border-amber-300 text-amber-800";
// Iter-74: overdue tasks get a red styling with a hard border so they stand
// out against every other calendar event colour.
const OVERDUE_COLOR = "bg-rose-100 border-rose-500 border-l-4 text-rose-900 ring-1 ring-rose-300";

function eventColor(evt) {
  if (evt.overdue || evt.meta?.overdue) return OVERDUE_COLOR;
  if (evt.kind === "job" || evt.kind === "job_task") return JOB_COLOR;
  if (evt.kind === "task") return TASK_COLOR;
  return TYPE_COLOR[evt.meta?.booking_type] || TYPE_COLOR.other;
}

function eventLabel(evt) {
  if (evt.kind === "job" || evt.kind === "job_task") return evt.title;
  if (evt.kind === "task") return evt.title;
  return evt.meta?.client_name || evt.title;
}

// A job_task spans its scheduled working-day segments — true when `k`
// (yyyy-mm-dd) falls inside any segment.
function jobTaskOnDay(evt, k) {
  const segs = evt.meta?.segments?.length
    ? evt.meta.segments
    : [{ start: evt.start.slice(0, 10), end: evt.end.slice(0, 10) }];
  return segs.some((s) => s.start <= k && k <= s.end);
}

const ROLE_LABEL = { super_admin: "Owner", admin: "Manager", sales: "Office", estimator: "Estimator", installer: "Installer" };

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toIso(dateStr, timeStr) {
  return new Date(`${dateStr}T${timeStr}`).toISOString();
}
function localHour(iso) {
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60;
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
}

const EMPTY_FORM = {
  id: null, booking_type: "site_quote", client_name: "", client_phone: "", client_email: "", client_address: "",
  company_id: "", assigned_to: "", date: "", start_time: "09:00", end_time: "10:00", notes: "", status: "booked", force: false,
  deal_id: "none", job_id: "none",
};

export default function Scheduler() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { user } = useAuth();
  const [day, setDay] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  // Default to "day" view on phone-sized screens — the 7-column week grid
  // is unusable at <=430px wide. Users can still switch to week/month manually.
  const [view, setView] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) return "day";
    return "week";
  });
  const [form, setForm] = useState(null);
  // Oct 2026 (Trent): Calendar ↔ Project (MS-Project-style Gantt) switch.
  const [mode, setModeState] = useState(() => localStorage.getItem("scheduler-mode") || "calendar");
  const setMode = (m) => { setModeState(m); localStorage.setItem("scheduler-mode", m); };
  // Crew-task chip click → open this task's editor in Project mode.
  const [focusTaskId, setFocusTaskId] = useState(null);
  // Project-view expand/collapse preference — saved via Set default ("expand" | "collapse" | null).
  const [projectExpand, setProjectExpand] = useState(null);
  // Jun 2026 (Trent): week/month assignee filter — [] means everyone.
  const [whoFilter, setWhoFilter] = useState([]);

  const dateStr = ymd(day);
  const range = useMemo(() => {
    if (view === "week") {
      const s = new Date(day); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); s.setHours(0, 0, 0, 0);
      const e = new Date(s); e.setDate(s.getDate() + 7);
      return { from: s, to: e };
    }
    if (view === "month") {
      return { from: new Date(day.getFullYear(), day.getMonth(), 1), to: new Date(day.getFullYear(), day.getMonth() + 1, 1) };
    }
    return { from: new Date(day), to: new Date(day.getTime() + 24 * 3600 * 1000) };
  }, [day, view]);

  const { data: team = [] } = useQuery({
    queryKey: ["team"],
    queryFn: async () => (await api.get("/auth/team")).data,
  });
  const { data: types = [] } = useQuery({
    queryKey: ["booking-types"],
    queryFn: async () => (await api.get("/bookings/types")).data,
  });
  const { data: bookings = [], isFetching } = useQuery({
    queryKey: ["bookings", view, range.from.toISOString()],
    queryFn: async () => (await api.get("/bookings", { params: { date_from: range.from.toISOString(), date_to: range.to.toISOString() } })).data,
  });
  // Unified event feed — bookings + install jobs + assigned tasks. Bookings
  // still fetched separately above so any legacy code paths (delete/complete)
  // keep working; but the calendar UI reads from `events` below.
  const { data: events = [] } = useQuery({
    queryKey: ["scheduler-events", view, range.from.toISOString()],
    queryFn: async () => (await api.get("/scheduler/events", { params: { date_from: range.from.toISOString(), date_to: range.to.toISOString() } })).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ["clients-picker"],
    queryFn: async () => (await api.get("/clients")).data,
    enabled: !!form,
    retry: false,
  });
  // Deal picker on the booking dialog. Iter-64: swapped from install-jobs to
  // OPEN DEALS because bookings (site quote / measure / call-back / repair)
  // happen at the deal stage — the install job doesn't exist until the deal
  // is Won. Only enabled while a booking form is open so we don't hammer the
  // endpoint on calendar navigation.
  const { data: allDeals = [] } = useQuery({
    queryKey: ["deals-open-picker"],
    queryFn: async () => (await api.get("/deals")).data,
    enabled: !!form, retry: false,
  });
  const openDeals = useMemo(
    () => {
      const list = (allDeals || []).filter((d) => d.stage !== "won" && d.stage !== "lost");
      // Iter-67: never lose sight of the deal the booking is currently
      // linked to — even if that deal has already moved to Won/Lost the
      // estimator must still see it in the picker so the linkage is
      // legible in the field.
      const linkedId = form?.deal_id && form.deal_id !== "none" && form.deal_id !== "" ? form.deal_id : null;
      if (linkedId && !list.some((d) => d.id === linkedId)) {
        const linked = (allDeals || []).find((d) => d.id === linkedId);
        if (linked) list.unshift(linked);
      }
      return list;
    },
    [allDeals, form?.deal_id],
  );

  const typeLabel = useMemo(() => ({ install: "Installation", installation: "Installation", ...Object.fromEntries(types.map((t) => [t.value, t.label])) }), [types]);
  // Jun 2026 (Trent): day view is PERSONAL — only the current user's column
  // and only their tasks, bookings and jobs (plus unassigned items in the
  // strip). Week/month respect the Who filter ([] = everyone).
  const visibleEvents = useMemo(() => {
    if (view === "day") {
      const uid = user?.id;
      return events.filter((e) => {
        if (e.kind === "task") return e.assigned_to === uid;
        if (e.kind === "job" || e.kind === "job_task") {
          const ids = e.meta?.installer_ids || [];
          return ids.length === 0 || ids.includes(uid);
        }
        return !e.assigned_to || e.assigned_to === uid;
      });
    }
    if (whoFilter.length === 0) return events;
    const sel = new Set(whoFilter);
    return events.filter((e) => {
      if (e.kind === "job" || e.kind === "job_task") return (e.meta?.installer_ids || []).some((id) => sel.has(id));
      return e.assigned_to && sel.has(e.assigned_to);
    });
  }, [events, view, whoFilter, user?.id]);
  // Day view renders a single column — the logged-in user only.
  const dayTeam = useMemo(() => team.filter((m) => m.id === user?.id), [team, user?.id]);
  const byMember = useMemo(() => {
    const m = {};
    // Only events with a resolvable team-member id sit in a member column.
    // Jobs render on EVERY assigned installer's column via meta.installer_ids
    // (crew bookings were removed Aug 2026).
    const teamIds = new Set(team.map((t) => t.id));
    for (const e of visibleEvents) {
      const instIds = (e.kind === "job" || e.kind === "job_task") ? (e.meta?.installer_ids || []).filter((id) => teamIds.has(id)) : [];
      if (instIds.length) {
        for (const uid of instIds) (m[uid] = m[uid] || []).push(e);
      } else if (e.assigned_to && teamIds.has(e.assigned_to)) {
        (m[e.assigned_to] = m[e.assigned_to] || []).push(e);
      }
    }
    return m;
  }, [visibleEvents, team]);
  const byDay = useMemo(() => {
    const m = {};
    const push = (d, e) => { const k = ymd(d); (m[k] = m[k] || []).push(e); };
    for (const e of visibleEvents) {
      if (e.kind === "job_task") {
        // Crew tasks appear only on their scheduled working days (segments
        // skip weekends + delay days) — this is the crew-utilisation view.
        const segs = e.meta?.segments?.length
          ? e.meta.segments
          : [{ start: e.start.slice(0, 10), end: e.end.slice(0, 10) }];
        for (const seg of segs) {
          const s = new Date(`${seg.start}T00:00:00`);
          const end = new Date(`${seg.end}T00:00:00`);
          let guard = 0;
          for (let d = new Date(s); d <= end && guard < 90; d.setDate(d.getDate() + 1), guard++) push(d, e);
        }
      } else if (e.kind === "job") {
        // Multi-day installs appear on EVERY day they span (Trent, Jun 2026)
        const s = new Date(e.start); s.setHours(0, 0, 0, 0);
        const end = new Date(e.end); end.setHours(0, 0, 0, 0);
        let guard = 0;
        for (let d = new Date(s); d <= end && guard < 90; d.setDate(d.getDate() + 1), guard++) push(d, e);
      } else {
        push(new Date(e.start), e);
      }
    }
    for (const k in m) m[k].sort((a, b) => a.start.localeCompare(b.start));
    return m;
  }, [visibleEvents]);
  // Events with no team-member assigned_to (typically jobs with crew names
  // instead of user ids). We render them in an "Unassigned" strip at the top
  // of the day view so they're not silently missing.
  const unassignedDayEvents = useMemo(() => {
    if (view !== "day") return [];
    const teamIds = new Set(team.map((t) => t.id));
    return visibleEvents.filter((e) => {
      if ((e.kind === "job" || e.kind === "job_task") && (e.meta?.installer_ids || []).some((id) => teamIds.has(id))) return false;
      if (e.kind === "job_task") return jobTaskOnDay(e, dateStr);
      return (!e.assigned_to || !teamIds.has(e.assigned_to)) && ymd(new Date(e.start)) === dateStr;
    });
  }, [visibleEvents, team, view, dateStr]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bookings"] });
    qc.invalidateQueries({ queryKey: ["scheduler-events"] });
    qc.invalidateQueries({ queryKey: ["daily-tasks"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  };

  const onConflict = (e) => {
    const detail = e?.response?.data?.detail;
    if (e?.response?.status === 409) {
      setForm((f) => ({ ...f, force: true }));
      toast.warning(detail);
    } else toast.error(detail || "Could not save booking");
  };

  const save = useMutation({
    mutationFn: async (f) => {
      const payload = {
        booking_type: f.booking_type, client_name: f.client_name.trim(),
        client_phone: f.client_phone || null, client_email: f.client_email?.trim() || null,
        client_address: f.client_address || null, company_id: f.company_id || null,
        assigned_to: f.assigned_to, notes: f.notes || null, force: f.force,
        start: toIso(f.date, f.start_time), end: toIso(f.date, f.end_time),
        deal_id: f.deal_id && f.deal_id !== "none" ? f.deal_id : null,
        job_id: f.job_id && f.job_id !== "none" ? f.job_id : null,
      };
      if (f.id) return (await api.patch(`/bookings/${f.id}`, { ...payload, status: f.status })).data;
      return (await api.post("/bookings", payload)).data;
    },
    onSuccess: (data) => {
      invalidate();
      // Also refresh the Deals picker if we auto-created a deal
      if (data.auto_deal_created) {
        qc.invalidateQueries({ queryKey: ["deals"] });
        qc.invalidateQueries({ queryKey: ["deals-open-picker"] });
      }
      setForm(null);
      const dealPart = data.auto_deal_created ? " · new deal added to pipeline" : "";
      toast.success(`Booked ${data.client_name} with ${data.assigned_to_name} — added to their tasks & calendar${dealPart}${data.welcome_email_sent ? " · welcome email + portal link sent" : ""}`);
    },
    onError: onConflict,
  });

  const complete = useMutation({
    mutationFn: async (id) => (await api.patch(`/bookings/${id}`, { status: "completed" })).data,
    onSuccess: () => { invalidate(); setForm(null); toast.success("Marked completed"); },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/bookings/${id}`)).data,
    onSuccess: () => { invalidate(); setForm(null); toast.success("Booking removed"); },
  });

  const pickClient = (c) => {
    const pc = c.primary_contact;
    const a0 = (c.addresses || [])[0];
    const addr = c.address || (a0 ? [a0.line1, a0.suburb, a0.postcode].filter(Boolean).join(", ") : "");
    setForm((f) => ({
      ...f, company_id: c.id, client_name: c.name,
      client_phone: c.phone || pc?.mobile_phone || pc?.phone || "",
      client_email: c.email || pc?.email || "",
      client_address: addr || f.client_address,
    }));
  };

  const startQuote = () => {
    const p = new URLSearchParams();
    if (form.client_name) p.set("customer_name", form.client_name);
    if (form.client_email) p.set("customer_email", form.client_email);
    if (form.client_phone) p.set("customer_phone", form.client_phone);
    if (form.client_address) p.set("site_address", form.client_address);
    if (form.company_id) p.set("company_id", form.company_id);
    // Iter-67: carry the booking's linked deal through so the new quote
    // inherits the JN- number and appears under the same deal. Without
    // this, "Start Quote" would build an orphan quote unlinked from the
    // enquiry that started the pipeline.
    if (form.deal_id && form.deal_id !== "none") p.set("deal", form.deal_id);
    nav(`/quotes/new?${p.toString()}`);
  };

  const openNew = (memberId, hour, dateOverride) => {
    // Iter-73: empty-slot click no longer opens the Booking dialog — it opens
    // the New Activity dialog pre-filled with the clicked team member + date
    // + hour. Bookings can still be created by editing an existing one; new
    // scheduler entries are always activities so they flow into Daily Tasks
    // + the calendar automatically.
    qc.invalidateQueries({ queryKey: ["clients-picker"] });
    const dateStrPick = dateOverride || dateStr;
    const hh = String(hour ?? 9).padStart(2, "0");
    const dueAt = `${dateStrPick}T${hh}:00:00`;
    setNewActivity({
      type: "meeting", title: "", description: "", due_at: dueAt,
      deal_id: "none", company_id: null, contact_id: null,
      completed: false, priority: "medium",
      // Iter-73: only pre-fill when the click carried a member column
      // (day-view slot click). Week/Month day clicks leave it Unassigned.
      assigned_to: memberId || null,
    });
  };

  const openEdit = (b) => {
    qc.invalidateQueries({ queryKey: ["clients-picker"] });
    const s = new Date(b.start), e = new Date(b.end);
    setForm({
      id: b.id, booking_type: b.booking_type, client_name: b.client_name,
      client_phone: b.client_phone || "", client_email: b.client_email || "", client_address: b.client_address || "",
      company_id: b.company_id || "", assigned_to: b.assigned_to, date: ymd(s),
      start_time: `${String(s.getHours()).padStart(2, "0")}:${String(s.getMinutes()).padStart(2, "0")}`,
      end_time: `${String(e.getHours()).padStart(2, "0")}:${String(e.getMinutes()).padStart(2, "0")}`,
      notes: b.notes || "", status: b.status, force: false,
      deal_id: b.deal_id || "none", job_id: b.job_id || "none",
    });
  };

  // Unified click handler for the calendar events. Bookings open the full
  // editor; jobs open the quick-edit dialog; tasks open the task-reschedule
  // dialog. Each event kind carries `meta` from the /scheduler/events feed.
  const [jobDialog, setJobDialog] = useState(null);    // {id, ...meta}
  // Iter-67: state for the ad-hoc "New activity" flow launched from the
  // Scheduler header — replaces the hidden "New booking" button.
  const [newActivity, setNewActivity] = useState(null);
  const [taskDialog, setTaskDialog] = useState(null);  // {id, ...meta}

  // Drag-and-drop reschedule — day view drags to a new time slot (30-min snap)
  // and can move an event to a different team-member column (bookings/tasks
  // only — jobs stay on their crew). Week view drags to a different day but
  // keeps the same time-of-day per the user's spec.
  const reschedule = useMutation({
    mutationFn: async ({ kind, id, start, end, assigned_to }) => {
      if (kind === "booking") {
        const payload = { start, end };
        if (assigned_to) payload.assigned_to = assigned_to;
        // `force: true` is safe here — the server-side clash detector still
        // reports, we just don't want the drag to be silently blocked. If a
        // future UX wants a confirm-on-clash, wire it into a dialog.
        payload.force = true;
        return (await api.patch(`/bookings/${id}`, payload)).data;
      }
      if (kind === "job") {
        // Jobs are date-only under the hood (scheduled_start / scheduled_end
        // are yyyy-mm-dd strings on write). Slice to date.
        return (await api.patch(`/jobs/${id}`, {
          scheduled_start: start.slice(0, 10),
          scheduled_end: end.slice(0, 10),
        })).data;
      }
      if (kind === "job_task") {
        // Pin the crew task to the dropped day — planner recomputes the whole
        // graph and the job's dates follow the tasks.
        return (await api.patch(`/project/tasks/${id}`, { pinned_start: start.slice(0, 10) })).data;
      }
      if (kind === "task") {
        return (await api.patch(`/activities/${id}`, { due_at: start })).data;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduler-events"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["daily-tasks"] });
      qc.invalidateQueries({ queryKey: ["activities"] });
      qc.invalidateQueries({ queryKey: ["project-plan"] });
      qc.invalidateQueries({ queryKey: ["crew-costs"] });
      toast.success("Rescheduled");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reschedule"),
  });

  // Drag handlers — kept out of the render fn to avoid re-creating them per row.
  const handleDragStart = (evt) => (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify(evt));
    e.dataTransfer.effectAllowed = "move";
  };

  // Day view: dropping into a member column at a Y offset → new start time
  // (30-min snap), keep duration, reassign to that member if bookings/tasks.
  const handleDayDrop = (memberId) => (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    let evt;
    try { evt = JSON.parse(raw); } catch { return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    // hours since DAY_START, snapped to 30 min
    const hours = DAY_START + y / HOUR_PX;
    const snappedMin = Math.max(0, Math.round((hours - DAY_START) * 60 / 30) * 30);
    const newStart = new Date(day);
    newStart.setHours(DAY_START, 0, 0, 0);
    newStart.setMinutes(newStart.getMinutes() + snappedMin);
    const origStart = new Date(evt.start);
    const origEnd = new Date(evt.end);
    const duration = origEnd - origStart;
    const newEnd = new Date(newStart.getTime() + duration);
    reschedule.mutate({
      kind: evt.kind,
      id: evt.id,
      start: newStart.toISOString(),
      end: newEnd.toISOString(),
      // Reassign only for bookings + tasks (jobs carry crew names, not user ids)
      assigned_to: (evt.kind === "booking" || evt.kind === "task") && memberId !== evt.assigned_to ? memberId : undefined,
    });
  };

  // Week view: dropping into a day cell → same time-of-day on that day.
  const handleWeekDrop = (targetDate) => (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    let evt;
    try { evt = JSON.parse(raw); } catch { return; }
    const origStart = new Date(evt.start);
    const origEnd = new Date(evt.end);
    const duration = origEnd - origStart;
    const newStart = new Date(targetDate);
    newStart.setHours(origStart.getHours(), origStart.getMinutes(), 0, 0);
    const newEnd = new Date(newStart.getTime() + duration);
    reschedule.mutate({
      kind: evt.kind,
      id: evt.id,
      start: newStart.toISOString(),
      end: newEnd.toISOString(),
    });
  };

  const openEventEdit = (evt) => {
    if (evt.kind === "booking") {
      // Reconstruct a booking-shaped object for the existing openEdit
      openEdit({
        id: evt.id, booking_type: evt.meta?.booking_type,
        client_name: evt.meta?.client_name || evt.title,
        client_phone: evt.meta?.client_phone, client_email: evt.meta?.client_email,
        client_address: evt.meta?.client_address, company_id: evt.meta?.company_id,
        assigned_to: evt.assigned_to, start: evt.start, end: evt.end,
        notes: evt.meta?.notes, status: evt.status,
        deal_id: evt.meta?.deal_id, job_id: evt.meta?.job_id,
      });
    } else if (evt.kind === "job") {
      setJobDialog({ id: evt.id, title: evt.title, start: evt.start, end: evt.end, ...evt.meta, status: evt.status });
    } else if (evt.kind === "job_task") {
      // Crew-task chip → jump to Project mode and open that task's edit view.
      setFocusTaskId(evt.id);
      setMode("project");
    } else if (evt.kind === "task") {
      // Iter-67: fetch the full activity so ActivityDetailDialog can render
      // the linked Deal + Client + description + attachments — the previous
      // TaskQuickDialog only showed a stripped-down "task summary" and hid
      // the linked-enquiry context the user actually needs to click through.
      (async () => {
        try {
          const { data } = await api.get(`/activities/${evt.id}`);
          setTaskDialog(data);
        } catch (e) {
          // Fallback to the event's meta if the fetch fails so the click
          // never becomes a dead-end.
          setTaskDialog({
            id: evt.id,
            title: evt.title,
            due_at: evt.start,
            type: evt.meta?.task_type || "task",
            priority: evt.meta?.priority || "medium",
            description: evt.meta?.description || "",
            company_id: evt.meta?.company_id || null,
            deal_id: evt.meta?.deal_id || null,
            job_id: evt.meta?.job_id || null,
            assigned_to: evt.assigned_to || null,
            completed: false,
          });
        }
      })();
    }
  };

  const shiftDay = (n) => setDay((d) => {
    const x = new Date(d);
    if (view === "week") x.setDate(x.getDate() + 7 * n);
    else if (view === "month") x.setMonth(x.getMonth() + n);
    else x.setDate(x.getDate() + n);
    return x;
  });
  const isToday = ymd(new Date()) === dateStr;
  const headerLabel = view === "day"
    ? day.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })
    : view === "week"
      ? `${range.from.toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${new Date(range.to.getTime() - 1).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`
      : day.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  return (
    <div>
      <PageHeader
        title="Scheduler"
        subtitle="Book installers & estimators. Every booking lands in that person's Daily Tasks and calendar automatically."
        breadcrumb={["Sales", "Scheduler"]}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <PhoneCalendarButton />
            {/* Iter-67: New booking button hidden until the workflow is
                revisited. Estimators create a scheduler entry from
                Enquiries or via a Deal-linked Activity instead. */}
            {/* <Button data-testid="new-booking-btn" onClick={() => openNew()} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus size={16} className="mr-2" /> New booking
            </Button> */}
            <Button
              data-testid="scheduler-new-activity-btn"
              onClick={() => setNewActivity({
                type: "meeting", title: "", description: "", due_at: "",
                deal_id: "none", company_id: null, contact_id: null,
                completed: false, priority: "medium", assigned_to: null,
              })}
              className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              <Plus size={16} className="mr-2" /> New activity
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 space-y-4">
        {/* Calendar ↔ Project mode switch (Trent, Oct 2026) */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex border border-slate-300 rounded-sm overflow-hidden" data-testid="scheduler-mode-switcher">
            {["calendar", "project"].map((m) => (
              <button key={m} data-testid={`scheduler-mode-${m}`} onClick={() => setMode(m)}
                className={`px-4 h-9 text-xs font-bold uppercase tracking-wider transition-colors ${mode === m ? "bg-slate-900 text-cyan-300" : "bg-white text-slate-500 hover:text-slate-900"}`}>
                {m === "calendar" ? "Calendar" : "Project"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {mode === "project" && <div className="text-xs text-slate-400 stamp-label hidden sm:block">Install jobs · task plans · dependencies · delays</div>}
            <DefaultViewControl screen="scheduler"
              current={{ mode, view, ...(projectExpand ? { project_expand: projectExpand } : {}) }}
              onApply={(f) => { if (f.mode) setMode(f.mode); if (f.view) setView(f.view); if (f.project_expand) setProjectExpand(f.project_expand); }} />
          </div>
        </div>

        {mode === "project" && (
          <ProjectView
            focusTaskId={focusTaskId}
            onFocusHandled={() => setFocusTaskId(null)}
            defaultExpand={projectExpand}
            onExpandChange={setProjectExpand}
          />
        )}

        {mode === "calendar" && (<>
        {/* Date nav */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex border border-slate-200 rounded-sm overflow-hidden" data-testid="scheduler-view-switcher">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`scheduler-view-${v}`} onClick={() => setView(v)}
                className={`px-3 h-9 text-xs font-semibold uppercase tracking-wider transition-colors ${view === v ? "bg-cyan-600 text-white" : "bg-white text-slate-500 hover:text-slate-900"}`}>
                {v}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => shiftDay(-1)} data-testid="scheduler-prev-day"><ChevronLeft size={16} /></Button>
          {view !== "day" && <WhoFilter team={team} me={user?.id} value={whoFilter} onChange={setWhoFilter} />}
          <div className="font-display font-semibold text-lg text-slate-900 min-w-[180px]" data-testid="scheduler-date">
            {headerLabel}
            {view === "day" && isToday && <Badge variant="outline" className="ml-2 rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-700">TODAY</Badge>}
          </div>
          <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => shiftDay(1)} data-testid="scheduler-next-day"><ChevronRight size={16} /></Button>
          {!isToday && <Button variant="ghost" size="sm" className="rounded-sm h-9 text-cyan-600" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setDay(d); }}>Back to today</Button>}
          <div className="text-xs text-slate-400 stamp-label ml-auto">{visibleEvents.length} event{visibleEvents.length === 1 ? "" : "s"} · {isFetching ? "refreshing…" : "up to date"}</div>
        </div>

        {/* Legend — 3 event kinds, one calendar */}
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-600" data-testid="scheduler-legend">
          <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-cyan-100 border-l-2 border-cyan-500" /> Bookings</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border-l-2 border-emerald-500" /> Install crew tasks (JN · job · task · crew)</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-amber-50 border-l-2 border-amber-300" /> Assigned tasks{view === "day" ? " (yours only)" : ""}</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-rose-100 border-l-4 border-rose-500" /> Overdue — shown in today&apos;s column</div>
        </div>

        {/* Availability grid (day view) */}
        {view === "day" && unassignedDayEvents.length > 0 && (
          <Card className="rounded-sm border-slate-200 industrial-shadow p-3" data-testid="unassigned-events-strip">
            <div className="stamp-label text-slate-500 mb-2">On today · not on a specific team member</div>
            <div className="flex flex-wrap gap-2">
              {unassignedDayEvents.map((evt) => (
                <button
                  key={`u-${evt.kind}-${evt.id}`}
                  onClick={() => openEventEdit(evt)}
                  className={`text-left rounded-sm border-l-4 px-2 py-1 shadow-sm max-w-[260px] ${eventColor(evt)}`}
                  data-testid={`unassigned-event-${evt.kind}-${evt.id}`}
                >
                  <div className="text-[11px] font-semibold truncate">
                    {evt.kind === "job" || evt.kind === "job_task" ? "🔨 " : evt.kind === "task" ? "✓ " : ""}
                    {eventLabel(evt)}
                  </div>
                  <div className="text-[10px] opacity-80 truncate">
                    {evt.kind === "job_task" ? `Task ${evt.meta?.task_no} · ${evt.meta?.task_name || ""}`
                     : evt.kind === "job" ? (evt.meta?.crew?.join(", ") || "Install")
                     : evt.kind === "task" ? "Task"
                     : (typeLabel[evt.meta?.booking_type] || evt.meta?.booking_type || "Booking")}
                  </div>
                  {evt.kind === "job_task" ? (
                    <span className="inline-block mt-0.5 rounded-sm bg-emerald-200/80 text-emerald-900 text-[9.5px] font-bold px-1 py-px">⛑ {evt.meta?.crew?.join(", ")}</span>
                  ) : (
                    <div className="text-[10px] font-mono opacity-70">{fmtTime(evt.start)}–{fmtTime(evt.end)}</div>
                  )}
                </button>
              ))}
            </div>
          </Card>
        )}
        {view === "day" && (
        <Card className="rounded-sm industrial-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[320px]">
              {/* Header row — day view is personal: current user only */}
              <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: `56px repeat(${dayTeam.length || 1}, minmax(160px, 1fr))` }}>
                <div />
                {dayTeam.map((m) => (
                  <div key={m.id} className="px-3 py-2.5 border-l border-slate-200" data-testid={`member-col-${m.email}`}>
                    <div className="font-display font-semibold text-sm text-slate-900 truncate">{m.name} (you)</div>
                    <div className="stamp-label text-slate-500">{ROLE_LABEL[m.role] || m.role} · {(byMember[m.id] || []).length ? `${(byMember[m.id] || []).length} booked` : "free all day"}</div>
                  </div>
                ))}
              </div>
              {/* Time grid */}
              <div className="grid" style={{ gridTemplateColumns: `56px repeat(${dayTeam.length || 1}, minmax(160px, 1fr))` }}>
                {/* Gutter */}
                <div>
                  {HOURS.map((h) => (
                    <div key={h} className="text-[10px] text-slate-400 font-mono text-right pr-2 border-b border-slate-100" style={{ height: HOUR_PX }}>
                      {h <= 12 ? `${h}am` : `${h - 12}pm`}
                    </div>
                  ))}
                </div>
                {dayTeam.map((m) => (
                  <div
                    key={m.id}
                    className="relative border-l border-slate-200"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDayDrop(m.id)}
                    data-testid={`day-column-${m.id}`}
                  >
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        onClick={() => openNew(m.id, h)}
                        className="border-b border-slate-100 hover:bg-cyan-50/60 cursor-pointer transition-colors"
                        style={{ height: HOUR_PX }}
                        data-testid={`slot-${m.email}-${h}`}
                      />
                    ))}
                    {(() => {
                      const colEvents = byMember[m.id] || [];
                      let odIdx = 0;
                      return colEvents.map((evt) => {
                      // Overdue tasks pin to the 7am row (display only — the
                      // activity's real due time is NOT changed). Stacked with
                      // a small offset so several overdue chips stay readable.
                      const isOverdueTask = evt.kind === "task" && (evt.overdue || evt.meta?.overdue);
                      const myOd = isOverdueTask ? odIdx++ : 0;
                      const startH = isOverdueTask ? 7 : localHour(evt.start);
                      const endH = isOverdueTask ? 7.5 : localHour(evt.end);
                      const top = Math.max((startH - DAY_START) * HOUR_PX, 0) + (isOverdueTask ? myOd * 16 : 0);
                      const height = Math.max((endH - Math.max(startH, DAY_START)) * HOUR_PX, 24);
                      const isCompleted = evt.status === "completed";
                      return (
                        <button
                          key={`${evt.kind}-${evt.id}`}
                          onClick={(e) => { e.stopPropagation(); openEventEdit(evt); }}
                          draggable
                          onDragStart={handleDragStart(evt)}
                          className={`absolute left-1 right-1 rounded-sm border-l-4 px-2 py-1 text-left overflow-hidden shadow-sm cursor-move ${eventColor(evt)} ${isCompleted ? "opacity-50" : ""}`}
                          style={{ top, height }}
                          data-testid={`event-${evt.kind}-${evt.id}`}
                        >
                          <div className="text-[11px] font-semibold truncate flex items-center gap-1">
                            {(evt.overdue || evt.meta?.overdue) && (
                              <span className="inline-flex items-center gap-0.5 rounded-sm bg-rose-600 text-white px-1 text-[8.5px] font-bold shrink-0">
                                <AlertOctagon size={8} /> OVERDUE
                              </span>
                            )}
                            <span className="truncate">
                              {evt.kind === "job" || evt.kind === "job_task" ? "🔨 " : evt.kind === "task" ? "✓ " : ""}
                              {eventLabel(evt)}{isCompleted ? " ✓" : ""}
                            </span>
                          </div>
                          <div className="text-[10px] truncate opacity-80">
                            {evt.kind === "booking" ? (typeLabel[evt.meta?.booking_type] || evt.meta?.booking_type)
                             : evt.kind === "job_task" ? `Task ${evt.meta?.task_no} · ${evt.meta?.task_name || ""}`
                             : evt.kind === "job" ? `Install · ${evt.meta?.progress_pct || 0}% done`
                             : (evt.overdue || evt.meta?.overdue) ? `${evt.days_overdue || evt.meta?.days_overdue}d overdue`
                             : "Task"}
                          </div>
                          <div className="text-[10px] font-mono opacity-70">
                            {evt.kind === "job_task"
                              ? <span className="font-sans font-bold text-emerald-800">⛑ {evt.meta?.crew?.join(", ")}</span>
                              : isOverdueTask
                                ? `due ${fmtTime(evt.original_due_at || evt.meta?.original_due_at || evt.start)}`
                                : `${fmtTime(evt.start)}–${fmtTime(evt.end)}`}
                          </div>
                        </button>
                      );
                      });
                    })()}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {dayTeam.length === 0 && (
            <div className="p-10 text-center text-slate-400 text-sm">Your calendar column isn&apos;t available — your account may still be pending approval.</div>
          )}
        </Card>
        )}

        {view === "week" && (
          <WeekView
            range={range}
            byDay={byDay}
            team={team}
            onEventClick={openEventEdit}
            onDayClick={(d) => openNew(null, 9, ymd(d))}
            onDayOpen={(d) => { setDay(d); setView("day"); }}
            typeLabel={typeLabel}
            onDragStart={handleDragStart}
            onWeekDrop={handleWeekDrop}
          />
        )}
        {view === "month" && (
          <MonthView
            day={day}
            byDay={byDay}
            onEventClick={openEventEdit}
            onDayOpen={(d) => openNew(null, 9, ymd(d))}
          />
        )}
        </>)}
      </div>

      {/* Booking dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        {/* Iter-67 mobile fix: cap dialog height at 92vh, run it as a flex
            column so the body scrolls internally while the footer stays
            visible. `pb-[env(safe-area-inset-bottom)]` keeps the Save /
            Remove / Complete buttons clear of the iPhone home-indicator. */}
        <DialogContent className="rounded-sm sm:max-w-lg w-[calc(100%-1rem)] p-0 max-h-[92vh] flex flex-col overflow-hidden" data-testid="booking-dialog">
          <DialogHeader className="px-4 pt-4 pb-2 shrink-0 border-b border-slate-100">
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <CalendarClock size={18} className="text-cyan-600" /> {form?.id ? "Edit booking" : "New booking"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-3">
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="stamp-label text-slate-600">Assign to</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v, force: false })}>
                    <SelectTrigger className="mt-1 rounded-sm h-10" data-testid="booking-assignee"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
                    <SelectContent>
                      {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {ROLE_LABEL[m.role] || m.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="stamp-label text-slate-600">Booking type</Label>
                  <Select value={form.booking_type} onValueChange={(v) => setForm({ ...form, booking_type: v })}>
                    <SelectTrigger className="mt-1 rounded-sm h-10" data-testid="booking-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["install", "installation"].includes(form.booking_type) && (
                        <SelectItem value={form.booking_type}>Installation (install crew)</SelectItem>
                      )}
                      {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Client — pick from database or type below</Label>
                <ClientPicker clients={clients} onPick={pickClient} />
              </div>

              {/* Deal / job linkage (iter-61, revised iter-62) —
                  · Tick if booking is for an existing open install job → inherit its deal
                  · Otherwise (default) a new deal is auto-created on save and the
                    booking is linked to it (server-side; see scheduler_routes.py)
                  · Installation bookings are NOT offered here — they're managed
                    from the Install Jobs section by setting scheduled dates on
                    the job record itself.
              */}
              {(() => {
                // Iter-64: bookings link to an OPEN DEAL (not an install job).
                // Deals live in the pipeline before Won, which is when the
                // install job is created. linked === true whenever the user
                // has chosen to attach this booking to an existing deal.
                // We keep "none" as the explicit "no link" sentinel and an
                // empty string means "linked but no deal picked yet".
                const linked = form.deal_id !== "none";
                const toggleLinked = () => setForm({
                  ...form,
                  deal_id: linked ? "none" : "",
                  // when we un-link, also drop the job_id so the two
                  // never drift out of sync
                  job_id: linked ? "none" : form.job_id,
                });
                return (
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60 space-y-2.5" data-testid="booking-link-panel">
                    {/* Radix Checkbox is a sibling of the descriptive text (not
                        nested inside a <label>) to avoid the browser double-
                        dispatching the click and cancelling the toggle. */}
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={linked}
                        onCheckedChange={(v) => setForm({
                          ...form,
                          deal_id: v ? "" : "none",
                          job_id: v ? form.job_id : "none",
                        })}
                        data-testid="booking-relates-to-job-cb"
                        className="mt-0.5"
                      />
                      <div
                        className="text-xs text-slate-700 leading-tight cursor-pointer select-none"
                        onClick={toggleLinked}
                        data-testid="booking-relates-to-job-label"
                      >
                        <div className="font-semibold text-slate-900">This booking relates to an existing open deal</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Tick if it's a call-back / measure / site quote / repair for a deal that's already in the pipeline.
                          Otherwise a new deal will be created and linked when you save.
                        </div>
                      </div>
                    </div>
                    {linked && (
                      <Select value={form.deal_id === "none" ? "" : form.deal_id} onValueChange={(v) => {
                        const d = openDeals.find((x) => x.id === v);
                        if (!d) { setForm({ ...form, deal_id: v }); return; }
                        setForm((f) => ({
                          ...f,
                          deal_id: v,
                          job_id: "none",
                          company_id: d.company_id || f.company_id,
                          client_address: d.address || f.client_address,
                          // Prefer the deal title as the booking title when
                          // the estimator hasn't already typed one — makes the
                          // paired Daily Task instantly identifiable.
                          title: f.title || d.title || f.title,
                        }));
                      }}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="booking-deal-picker">
                          <SelectValue placeholder="Pick a deal..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {openDeals.length === 0 && (
                            <div className="px-3 py-2 text-xs text-slate-500">No open deals — untick and a new one will be created on save.</div>
                          )}
                          {openDeals.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              <span className="font-mono text-xs font-semibold">{d.job_number || "—"}</span>
                              <span className="text-xs ml-2">· {d.title}</span>
                              {d.stage && (
                                <span className="text-[10px] text-slate-400 ml-2">· {d.stage}</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!linked && (
                      <div className="text-[11px] text-cyan-700 bg-cyan-50/80 border border-cyan-200 rounded-sm px-2 py-1.5" data-testid="auto-deal-notice">
                        <b>New deal will be created</b> when you save — linked to this booking with a fresh JN- number.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Iter-67: rich linked-client / deal card. Only rendered
                  when editing an existing booking that has a linked deal
                  or client — gives the field estimator instant access to
                  phone / address / notes / latest quote without leaving
                  the calendar. */}
              {form?.id && (form.deal_id !== "none" || form.company_id) && (
                <LinkedClientCard bookingId={form.id} onNavClose={() => setForm(null)} />
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="stamp-label text-slate-600">Client name</Label>
                  <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value, company_id: "" })} className="mt-1 rounded-sm h-10" data-testid="booking-client-name" placeholder="John Smith" />
                </div>
                <div>
                  <Label className="stamp-label text-slate-600"><Phone size={10} className="inline mr-1" />Phone</Label>
                  <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="mt-1 rounded-sm h-10" data-testid="booking-client-phone" placeholder="0400 000 000" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="stamp-label text-slate-600">Client email</Label>
                  <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="mt-1 rounded-sm h-10" data-testid="booking-client-email" placeholder="client@email.com" />
                  <p className="text-[10px] text-slate-400 mt-1">New bookings auto-send a welcome email + client portal link.</p>
                </div>
                <div>
                  <Label className="stamp-label text-slate-600"><MapPin size={10} className="inline mr-1" />Site address</Label>
                  <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="mt-1 rounded-sm h-10" data-testid="booking-client-address" placeholder="12 Fence St, Erina NSW" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="stamp-label text-slate-600">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, force: false })} className="mt-1 rounded-sm h-10" data-testid="booking-date" />
                </div>
                <div>
                  <Label className="stamp-label text-slate-600">Start</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value, force: false })} className="mt-1 rounded-sm h-10" data-testid="booking-start" />
                </div>
                <div>
                  <Label className="stamp-label text-slate-600">End</Label>
                  <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value, force: false })} className="mt-1 rounded-sm h-10" data-testid="booking-end" />
                </div>
              </div>
              <div>
                <Label className="stamp-label text-slate-600">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="booking-notes" placeholder="Gate code, dogs on site, quote reference…" />
              </div>
              {form.force && <p className="text-xs text-amber-600 font-medium">Clash warning shown — saving again will double-book this person.</p>}
              {form.id && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="stamp-label text-slate-500 mb-2">Pipeline — move {form.client_name || "this client"} forward</div>
                  {/* Iter-67: Start Job removed — the estimator starts a
                      Quote from a booking; Jobs are only created from a
                      won Deal via the standard Deal → Won → Install flow.
                      Start Quote inherits the booking's deal (JN-) via
                      the `?deal=` URL param above, so the new quote sits
                      under the same enquiry pipeline. */}
                  <Button
                    variant="outline"
                    onClick={startQuote}
                    className="rounded-sm h-9 w-full border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="start-quote-from-booking-btn"
                  >
                    <FileText size={14} className="mr-1.5" />
                    Start Quote{form.deal_id && form.deal_id !== "none" ? " · linked to this deal (JN-)" : ""}
                  </Button>
                </div>
              )}
            </div>
          )}
          </div>
          <DialogFooter className="gap-2 flex-wrap px-4 py-3 shrink-0 border-t border-slate-100 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {form?.id && (
              <>
                <Button variant="outline" onClick={() => del.mutate(form.id)} disabled={del.isPending} className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50 mr-auto" data-testid="delete-booking-btn">
                  <Trash2 size={14} className="mr-1" /> Remove
                </Button>
                {form.status !== "completed" && (
                  <Button variant="outline" onClick={() => complete.mutate(form.id)} disabled={complete.isPending} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="complete-booking-btn">
                    <Check size={14} className="mr-1" /> Completed
                  </Button>
                )}
              </>
            )}
            <Button onClick={() => save.mutate(form)} disabled={save.isPending || !form?.client_name?.trim() || !form?.assigned_to}
              data-testid="save-booking-btn" className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
              {save.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <CalendarClock size={16} className="mr-2" />}
              {form?.force ? "Save anyway" : "Save booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Install-job quick-edit — opened when a job event on the calendar is clicked */}
      <JobQuickDialog
        job={jobDialog}
        onClose={() => setJobDialog(null)}
        onSaved={() => { invalidate(); setJobDialog(null); }}
      />

      {/* Task detail — Iter-67: uses the same rich ActivityDetailDialog as
          Daily Tasks / Deals / Activities, so the linked Deal + client card
          are visible instead of just title/date/priority. */}
      <ActivityDetailDialog
        activity={taskDialog}
        open={!!taskDialog}
        onClose={() => setTaskDialog(null)}
        onSaved={() => { invalidate(); setTaskDialog(null); }}
      />

      {/* Iter-67: ad-hoc "New activity" (defaults to meeting) launched from
          the Scheduler page header. Uses the same dialog / endpoints as the
          Activities page so the new row shows up on Daily Tasks and, if the
          user later assigns it a due date, on the Scheduler feed. */}
      <ActivityDetailDialog
        activity={newActivity}
        open={!!newActivity}
        onClose={() => setNewActivity(null)}
        onSaved={() => { invalidate(); setNewActivity(null); }}
      />
    </div>
  );
}

function JobQuickDialog({ job, onClose, onSaved }) {
  const nav = useNavigate();
  const [draft, setDraft] = useState(null);
  // Sync local state whenever a new job is passed in
  useMemo(() => {
    if (!job) { setDraft(null); return null; }
    setDraft({
      id: job.id,
      title: job.title,
      scheduled_start: (job.scheduled_start || job.start || "").slice(0, 10),
      scheduled_end: (job.scheduled_end || job.end || "").slice(0, 10),
      crew: (job.crew || []).join(", "),
      site_address: job.site_address || "",
      progress_pct: job.progress_pct || 0,
      status: job.status,
    });
    return null;
  }, [job]);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/jobs/${draft.id}`, {
      scheduled_start: draft.scheduled_start || null,
      scheduled_end: draft.scheduled_end || null,
      crew: draft.crew ? draft.crew.split(",").map((c) => c.trim()).filter(Boolean) : [],
      site_address: draft.site_address || null,
    })).data,
    onSuccess: () => { toast.success("Install job updated"); onSaved?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save job"),
  });

  if (!job || !draft) return null;
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="job-quick-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Hammer size={18} className="text-emerald-600" /> Install job
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm font-medium text-slate-900">{draft.title}</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="stamp-label text-slate-600">Start date</Label>
              <Input type="date" value={draft.scheduled_start} onChange={(e) => setDraft({ ...draft, scheduled_start: e.target.value })} className="mt-1 rounded-sm h-9" data-testid="job-quick-start" />
            </div>
            <div>
              <Label className="stamp-label text-slate-600">End date</Label>
              <Input type="date" value={draft.scheduled_end} onChange={(e) => setDraft({ ...draft, scheduled_end: e.target.value })} className="mt-1 rounded-sm h-9" data-testid="job-quick-end" />
            </div>
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Crew (comma separated)</Label>
            <Input value={draft.crew} onChange={(e) => setDraft({ ...draft, crew: e.target.value })} className="mt-1 rounded-sm h-9" placeholder="Mick, Jake" data-testid="job-quick-crew" />
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Site address</Label>
            <Input value={draft.site_address} onChange={(e) => setDraft({ ...draft, site_address: e.target.value })} className="mt-1 rounded-sm h-9" data-testid="job-quick-address" />
          </div>
          <div className="bg-slate-50 rounded-sm p-2 text-xs text-slate-600 flex items-center justify-between">
            <span>Progress</span>
            <span className="font-mono font-semibold text-slate-900">{job.progress_pct == null ? "—" : `${draft.progress_pct}%`}</span>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav(`/jobs/${draft.id}`)} className="rounded-sm" data-testid="job-open-detail">
              Open job
            </Button>
            <Button variant="outline" onClick={() => nav(`/jobs/${draft.id}`)} className="rounded-sm" data-testid="job-open-progress">
              Progress
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="job-quick-save">
              {save.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Iter-67: LinkedClientCard ----------
// Rich "who is this booking for" card. Renders inside the booking edit
// dialog when the booking already exists AND has a linked deal or client.
// One /api/bookings/{id}/context call fetches everything the estimator
// needs in the field: deal number + stage, company + primary contact
// (with tel: / mailto: links), address (Google Maps deep link), booking
// notes, latest quote, and quick nav to the full Deal / Client pages.
function LinkedClientCard({ bookingId, onNavClose }) {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["booking-context", bookingId],
    queryFn: async () => (await api.get(`/bookings/${bookingId}/context`)).data,
    enabled: !!bookingId,
  });
  if (isLoading || !data) {
    return (
      <div className="border border-slate-200 rounded-sm bg-slate-50/60 p-3 text-xs text-slate-500 flex items-center gap-2" data-testid="linked-client-card-loading">
        <Loader2 size={13} className="animate-spin" /> Loading client info…
      </div>
    );
  }
  const { deal, company, contact, activity, latest_quote, booking } = data;
  if (!deal && !company) return null;

  const phone = contact?.mobile_phone || contact?.phone || booking?.client_phone || "";
  const email = contact?.email || booking?.client_email || "";
  const address = booking?.client_address || deal?.address || "";
  const contactName = contact ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim() : "";
  const stageLabel = (deal?.stage || "").replace(/_/g, " ");
  const stageColour = {
    enquiry: "bg-slate-100 text-slate-700 border-slate-300",
    site_quote: "bg-amber-50 text-amber-800 border-amber-300",
    qualified: "bg-cyan-50 text-cyan-800 border-cyan-300",
    quoted: "bg-cyan-50 text-cyan-800 border-cyan-300",
    won: "bg-emerald-50 text-emerald-800 border-emerald-300",
    lost: "bg-rose-50 text-rose-800 border-rose-300",
  }[deal?.stage] || "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="border border-cyan-300 rounded-sm bg-gradient-to-br from-cyan-50/70 to-white p-3 space-y-3" data-testid="linked-client-card">
      {/* Deal row */}
      {deal && (
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            className="flex items-start gap-2 min-w-0 text-left group"
            onClick={() => { onNavClose?.(); nav(`/deals?open=${deal.id}`); }}
            data-testid="linked-deal-link"
          >
            <Briefcase size={15} className="text-cyan-700 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-cyan-800 group-hover:underline truncate">
                {deal.job_number ? <span className="font-mono">{deal.job_number}</span> : null}
                <span className="truncate">· {deal.title || "Untitled deal"}</span>
                <ExternalLink size={11} className="shrink-0 opacity-60" />
              </div>
              {stageLabel && (
                <div className="mt-1">
                  <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${stageColour}`} data-testid="linked-deal-stage">{stageLabel}</span>
                </div>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Client / contact row */}
      {company && (
        <div className="flex items-start gap-2 pt-2 border-t border-cyan-200/70">
          <UserRound size={15} className="text-cyan-700 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <button
              type="button"
              className="text-sm font-semibold text-slate-900 hover:text-cyan-700 hover:underline truncate block text-left"
              onClick={() => { onNavClose?.(); nav(`/clients/${company.id}`); }}
              data-testid="linked-client-name"
            >
              {company.name}
            </button>
            {contactName && (
              <div className="text-xs text-slate-600">{contactName}</div>
            )}
          </div>
        </div>
      )}

      {/* Tappable phone / email / address grid — designed for one-tap field use */}
      <div className="grid gap-1.5 pt-1">
        {phone && (
          <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-xs text-slate-700 hover:text-cyan-700" data-testid="linked-client-phone">
            <Phone size={13} className="text-emerald-600 shrink-0" />
            <span className="font-mono">{phone}</span>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider ml-auto">tap to call</span>
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} className="flex items-center gap-2 text-xs text-slate-700 hover:text-cyan-700 truncate" data-testid="linked-client-email">
            <Mail size={13} className="text-cyan-600 shrink-0" />
            <span className="truncate">{email}</span>
          </a>
        )}
        {address && (
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs text-slate-700 hover:text-cyan-700"
            data-testid="linked-client-address"
          >
            <MapPin size={13} className="text-rose-600 shrink-0" />
            <span className="truncate">{address}</span>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider ml-auto">maps</span>
          </a>
        )}
      </div>

      {/* Booking notes + linked task + latest quote */}
      {(booking?.notes || activity?.description) && (
        <div className="text-xs text-slate-600 bg-white/70 border border-slate-200 rounded-sm p-2 flex items-start gap-1.5" data-testid="linked-client-notes">
          <StickyNote size={12} className="text-slate-400 shrink-0 mt-0.5" />
          <div className="whitespace-pre-wrap">{booking?.notes || activity?.description}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {activity && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-600 border border-slate-300 rounded-sm px-1.5 py-0.5" data-testid="linked-activity-badge">
            <ClipboardList size={10} /> Linked task
          </span>
        )}
        {latest_quote && (
          <button
            type="button"
            onClick={() => { onNavClose?.(); nav(`/quotes/v2/${latest_quote.id}`); }}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-cyan-700 border border-cyan-300 bg-cyan-50/50 rounded-sm px-1.5 py-0.5 hover:bg-cyan-100"
            data-testid="linked-quote-link"
          >
            <FileText size={10} /> {latest_quote.quote_number || "Quote"} · {latest_quote.status}
            {latest_quote.sale_price ? ` · $${Number(latest_quote.sale_price).toLocaleString()}` : ""}
          </button>
        )}
      </div>
    </div>
  );
}

function ClientPicker({ clients, onPick }) {  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between rounded-sm h-10 mt-1 font-normal text-slate-600" data-testid="booking-client-picker">
          <span className="flex items-center gap-2"><UserRound size={14} className="text-cyan-600" /> Pick from Client Database…</span>
          <ChevronsUpDown size={14} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[340px]" align="start">
        <Command>
          <CommandInput placeholder="Search clients…" data-testid="client-picker-search" />
          <CommandList>
            <CommandEmpty>No matching clients.</CommandEmpty>
            {clients.map((c) => (
              <CommandItem key={c.id} value={`${c.name} ${c.customer_number || ""} ${c.phone || ""}`} onSelect={() => { onPick(c); setOpen(false); }} data-testid={`client-pick-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{[c.customer_number, c.phone || c.primary_contact?.mobile_phone].filter(Boolean).join(" · ")}</div>
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function WhoFilter({ team, me, value, onChange }) {
  const [open, setOpen] = useState(false);
  const label = value.length === 0
    ? "Everyone"
    : value.length === 1
      ? (team.find((m) => m.id === value[0])?.name || "1 person")
      : `${value.length} people`;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="rounded-sm h-9 text-xs font-semibold" data-testid="scheduler-who-filter">
          <UserRound size={13} className="mr-1.5 text-cyan-600" /> {label}
          <ChevronsUpDown size={12} className="ml-1.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1.5 w-60" align="start">
        <button type="button" onClick={() => onChange([])}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-left hover:bg-cyan-50 ${value.length === 0 ? "bg-cyan-50 text-cyan-800 font-semibold" : "text-slate-700"}`}
          data-testid="who-filter-everyone">
          <Check size={14} className={value.length === 0 ? "text-cyan-600" : "opacity-0"} /> Everyone
        </button>
        <div className="h-px bg-slate-100 my-1" />
        {team.map((m) => (
          <button key={m.id} type="button" onClick={() => toggle(m.id)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm text-left hover:bg-cyan-50 ${value.includes(m.id) ? "text-slate-900 font-medium" : "text-slate-600"}`}
            data-testid={`who-filter-${m.id}`}>
            <Checkbox checked={value.includes(m.id)} className="pointer-events-none" />
            <span className="truncate">{m.name}{m.id === me ? " (you)" : ""}</span>
          </button>
        ))}
        <div className="text-[10px] text-slate-400 px-2 pt-1.5">Pick one or more people — or Everyone.</div>
      </PopoverContent>
    </Popover>
  );
}

function WeekView({ range, byDay, team, onEventClick, onDayClick, onDayOpen, typeLabel = {}, onDragStart, onWeekDrop }) {
  const teamName = Object.fromEntries(team.map((m) => [m.id, m.name]));
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(range.from); d.setDate(d.getDate() + i); return d; });
  const todayKey = ymd(new Date());
  return (
    <Card className="rounded-sm industrial-shadow overflow-hidden" data-testid="scheduler-week-view">
      <div className="overflow-x-auto">
        <div className="grid min-w-[840px]" style={{ gridTemplateColumns: "repeat(7, minmax(120px, 1fr))" }}>
          {days.map((d) => {
            const k = ymd(d);
            const isT = k === todayKey;
            return (
              <div key={k} className={`border-l first:border-l-0 border-slate-200 ${isT ? "bg-cyan-50/40" : ""}`}>
                <button
                  onClick={() => onDayOpen(d)}
                  className={`w-full px-2 py-2 border-b border-slate-200 text-left hover:bg-cyan-50 transition-colors ${isT ? "bg-cyan-100/60" : "bg-slate-50"}`}
                  data-testid={`week-day-${k}`}
                >
                  <div className="stamp-label text-slate-500">{d.toLocaleDateString("en-AU", { weekday: "short" })}</div>
                  <div className={`font-display font-semibold ${isT ? "text-cyan-700" : "text-slate-900"}`}>{d.getDate()}</div>
                </button>
                <div
                  className="p-1.5 space-y-1 min-h-[220px] cursor-pointer hover:bg-cyan-50/30 transition-colors"
                  onClick={() => onDayClick(d)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onWeekDrop ? onWeekDrop(d) : undefined}
                  data-testid={`week-day-slot-${k}`}
                >
                  {(byDay[k] || []).map((evt) => (
                    <button
                      key={`${evt.kind}-${evt.id}`}
                      onClick={(e) => { e.stopPropagation(); onEventClick(evt); }}
                      draggable={!!onDragStart}
                      onDragStart={onDragStart ? onDragStart(evt) : undefined}
                      className={`w-full text-left rounded-sm border-l-4 px-1.5 py-1 shadow-sm cursor-move ${eventColor(evt)} ${evt.status === "completed" ? "opacity-50" : ""}`}
                      data-testid={`event-${evt.kind}-${evt.id}`}
                    >
                      {evt.kind === "job_task" ? (
                        <>
                          <div className="text-[11px] font-semibold truncate">🔨 <span className="font-mono">{evt.meta?.job_number}</span> · {evt.meta?.job_title || evt.title}</div>
                          <div className="text-[10px] truncate opacity-80">Task {evt.meta?.task_no} · {evt.meta?.task_name}</div>
                          <span className="inline-block mt-0.5 rounded-sm bg-emerald-200/80 text-emerald-900 text-[9px] font-bold px-1 py-px truncate max-w-full">⛑ {evt.meta?.crew?.join(", ")}</span>
                        </>
                      ) : (
                        <>
                          {(evt.overdue || evt.meta?.overdue) ? (
                            <div className="text-[9px] font-bold text-rose-700 flex items-center gap-1">
                              <AlertOctagon size={9} /> {evt.days_overdue || evt.meta?.days_overdue}D OVERDUE
                            </div>
                          ) : (
                            <div className="text-[10px] font-mono opacity-70">{fmtTime(evt.start)}</div>
                          )}
                          <div className="text-[11px] font-semibold truncate">
                            {evt.kind === "task" ? "✓ " : ""}
                            {eventLabel(evt)}{evt.status === "completed" ? " ✓" : ""}
                          </div>
                          <div className="text-[10px] truncate opacity-80">{teamName[evt.assigned_to] || evt.assigned_to_name || ""}</div>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function MonthView({ day, byDay, onEventClick, onDayOpen }) {
  const first = new Date(day.getFullYear(), day.getMonth(), 1);
  const gridStart = new Date(first); gridStart.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const todayKey = ymd(new Date());
  return (
    <Card className="rounded-sm industrial-shadow overflow-hidden" data-testid="scheduler-month-view">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => (
          <div key={w} className="px-2 py-1.5 stamp-label text-slate-500 text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const k = ymd(d);
          const inMonth = d.getMonth() === day.getMonth();
          const isT = k === todayKey;
          const evts = byDay[k] || [];
          return (
            <div
              key={k}
              onClick={() => onDayOpen(d)}
              className={`min-h-[92px] border-b border-l first:border-l-0 border-slate-100 p-1 text-left cursor-pointer hover:bg-cyan-50/50 transition-colors ${inMonth ? "" : "bg-slate-50/60"}`}
              data-testid={`month-day-${k}`}
            >
              <div className={`text-[11px] font-mono mb-1 w-5 h-5 flex items-center justify-center rounded-full ${isT ? "bg-cyan-600 text-white font-bold" : inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</div>
              {evts.slice(0, 3).map((evt) => (
                <div
                  key={`${evt.kind}-${evt.id}`}
                  onClick={(e) => { e.stopPropagation(); onEventClick(evt); }}
                  className={`rounded-sm border-l-2 px-1 py-0.5 mb-0.5 text-[9px] font-semibold truncate ${eventColor(evt)} ${evt.status === "completed" ? "opacity-50" : ""}`}
                  data-testid={`event-${evt.kind}-${evt.id}`}
                >
                  {evt.kind === "job" || evt.kind === "job_task" ? "🔨 " : evt.kind === "task" ? "✓ " : ""}
                  {evt.kind === "job_task"
                    ? `${evt.meta?.job_number || ""} T${evt.meta?.task_no} · ${evt.meta?.crew?.join(", ") || ""}`
                    : <>{fmtTime(evt.start)} {eventLabel(evt)}</>}
                </div>
              ))}
              {evts.length > 3 && <div className="text-[9px] text-cyan-600 font-semibold">+{evts.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
